import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Stethoscope, Send, CheckCircle2, Phone, Building2, MapPin, User, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";

const FEATURES = [
  { title: "Gestion des Produits", desc: "Suivi du stock, alertes péremption, import automatique" },
  { title: "Destruction Sécurisée", desc: "Certificats PDF avec QR code, traçabilité complète" },
  { title: "Analyses & Rapports", desc: "KPIs, benchmarks, recommandations personnalisées" },
  { title: "Dr CMES (IA)", desc: "Assistant intelligent pour vous guider au quotidien" },
  { title: "PharmExchange", desc: "Réseau anonyme d'échange de produits entre pharmacies" },
  { title: "API Caisse", desc: "Connexion automatique avec votre logiciel de gestion" },
];

export default function QRLandingPage() {
  const [searchParams] = useSearchParams();
  const source = searchParams.get("src") || "qr";
  const destructionId = searchParams.get("id") || "";
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFeatures, setShowFeatures] = useState(false);
  const [form, setForm] = useState({
    pharmacy_name: "", contact_name: "", phone: "", city: "", commune: "",
    message: destructionId ? `Ref. destruction: ${destructionId}` : ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.pharmacy_name || !form.phone) {
      toast.error("Nom de la pharmacie et téléphone requis");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/public/emergency-request`, {
        ...form, request_type: source === "destruction" ? "intervention_destruction" : "intervention_urgence"
      });
      setSubmitted(true);
    } catch {
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center" data-testid="qr-success">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Demande enregistrée !</h2>
          <p className="text-gray-600 text-sm mb-6">
            CMES vous contactera au <strong>{form.phone}</strong> dans les plus brefs délais pour organiser l'intervention.
          </p>

          {!showFeatures ? (
            <button onClick={() => setShowFeatures(true)} className="w-full py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2" data-testid="discover-features-btn">
              Découvrir CMES INTEL-STOCK <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <div className="text-left space-y-3 mt-4" data-testid="features-list">
              <p className="text-sm font-semibold text-gray-700 mb-3">Avec CMES INTEL-STOCK, vous pouvez :</p>
              {FEATURES.map((f, i) => (
                <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg bg-emerald-50 border border-emerald-100">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-gray-800">{f.title}</p>
                    <p className="text-xs text-gray-500">{f.desc}</p>
                  </div>
                </div>
              ))}
              <a href="/register" className="block w-full py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors text-center mt-4" data-testid="create-account-btn">
                Créer mon compte gratuitement
              </a>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4" data-testid="qr-landing-page">
      <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Stethoscope className="w-7 h-7 text-emerald-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-800" data-testid="qr-title">CMES INTEL-STOCK</h1>
          <p className="text-sm text-gray-500 mt-1">Demande d'intervention d'urgence</p>
          <a href="tel:+2250768127000" className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium hover:bg-emerald-100 transition-colors">
            <Phone className="w-3 h-3" /> 07 68 12 70 00
          </a>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-1">
              <Building2 className="w-3 h-3" /> Nom de la pharmacie / établissement *
            </label>
            <input
              value={form.pharmacy_name}
              onChange={e => setForm({ ...form, pharmacy_name: e.target.value })}
              placeholder="Ex: Pharmacie du Plateau"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
              required
              data-testid="qr-pharmacy-name"
            />
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-1">
              <User className="w-3 h-3" /> Nom du contact
            </label>
            <input
              value={form.contact_name}
              onChange={e => setForm({ ...form, contact_name: e.target.value })}
              placeholder="Ex: Dr Kouamé"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
              data-testid="qr-contact-name"
            />
          </div>

          <div>
            <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-1">
              <Phone className="w-3 h-3" /> Numéro de téléphone *
            </label>
            <input
              value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })}
              placeholder="Ex: +225 07 00 00 00"
              type="tel"
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
              required
              data-testid="qr-phone"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-1">
                <MapPin className="w-3 h-3" /> Ville
              </label>
              <input
                value={form.city}
                onChange={e => setForm({ ...form, city: e.target.value })}
                placeholder="Ex: Abidjan"
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
                data-testid="qr-city"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Commune</label>
              <input
                value={form.commune}
                onChange={e => setForm({ ...form, commune: e.target.value })}
                placeholder="Ex: Cocody"
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400"
                data-testid="qr-commune"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">Message (optionnel)</label>
            <textarea
              value={form.message}
              onChange={e => setForm({ ...form, message: e.target.value })}
              placeholder="Décrivez brièvement votre besoin..."
              rows={2}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 resize-none"
              data-testid="qr-message"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="qr-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {loading ? "Envoi en cours..." : "Envoyer ma demande"}
          </button>
        </form>

        <p className="text-[10px] text-gray-400 text-center mt-4">
          CMES — Coaching Médical et Épanouissement Social | Tel: 07 68 12 70 00
        </p>
      </div>
    </div>
  );
}
